import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useContext, useState } from 'react'
import JBWDateLabel from './JBWDateLabel'
import { AuthContext } from '../AuthContext'
import { claimsAdTools } from '../services/ClaimsServices'

const DuplicateClaimDialog = ({ claimData, invoice, onClose, openDialog }) => {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const { user } = useContext(AuthContext)

  useState(() => {
    setOpen(openDialog)
  }, [openDialog])

  const handleClose = () => {
    setOpen(false)
    setSaveError('')
    onClose()
  }

  const handleDuplicateClaim = async () => {
    const claimId = invoice?.invoiceClaims?.[0]?.id
    if (!claimId) {
      setSaveError('Could not determine claim ID to duplicate.')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      await claimsAdTools(claimId, 'duplicate', user?.name || user?.username || user?.email)
      setSaving(false)
      handleClose()
    } catch (err) {
      setSaving(false)
      setSaveError(err?.response?.data?.message || 'Failed to duplicate claim. Please try again.')
    }
  }

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogTitle>Duplicate Claim</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError('')}>
              {saveError}
            </Alert>
          )}

          {/* Invoice Details */}
          {invoice && (
            <Paper elevation={3} sx={{ p: 1 }}>
              <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, fontWeight: 'bold' }}>
                Invoice Details
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Invoice Details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell>Job Number</TableCell>
                      <TableCell>Customer Name</TableCell>
                      <TableCell>Phone &amp; Email</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>ST Created On</TableCell>
                      <TableCell>ST Modified On</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{invoice.invoiceId}</TableCell>
                      <TableCell>{invoice.invoiceStatus}</TableCell>
                      <TableCell>{invoice.invoiceSummary}</TableCell>
                      <TableCell>{invoice.jobNumber}</TableCell>
                      <TableCell>
                        {invoice.firstName} {invoice.lastName}
                      </TableCell>
                      <TableCell>
                        {invoice.phoneNumber && invoice.phoneNumber !== 'null'
                          ? invoice.phoneNumber
                          : ''}
                        {invoice.emailAddress && invoice.emailAddress !== 'null'
                          ? `, ${invoice.emailAddress}`
                          : ''}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                          {`${invoice.addressLine1 || ''}${invoice.addressLine2 && invoice.addressLine2 !== 'null' ? ', ' + invoice.addressLine2 : ''}${invoice.city && invoice.city !== 'null' ? ', ' + invoice.city : ''}${invoice.state && invoice.state !== 'null' ? ', ' + invoice.state : ''}${invoice.zipcode && invoice.zipcode !== 'null' ? ', ' + invoice.zipcode : ''}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {invoice.stCreatedOn && (
                          <JBWDateLabel inputDate={invoice.stCreatedOn} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {invoice.stModifiedOn && (
                          <JBWDateLabel inputDate={invoice.stModifiedOn} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Location Agreement */}
          {claimData && (
            <Paper elevation={3} sx={{ p: 1 }}>
              <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, fontWeight: 'bold' }}>
                Agreement
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Location Agreement Details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Code</TableCell>
                      <TableCell>Agreement Id</TableCell>
                      <TableCell>Agreement Status</TableCell>
                      <TableCell>Agreement Link</TableCell>
                      <TableCell>Purchase Date</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Coverage Start Date</TableCell>
                      <TableCell>Expiration Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{claimData.productCode}</TableCell>
                      <TableCell>{claimData.jbwAgreementId}</TableCell>
                      <TableCell>{claimData.agreementStatus}</TableCell>
                      <TableCell>{claimData.agreementLink}</TableCell>
                      <TableCell>
                        {claimData.purchaseDate && (
                          <JBWDateLabel inputDate={claimData.purchaseDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {claimData.startDate && (
                          <JBWDateLabel inputDate={claimData.startDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {claimData.coverageStartDate && (
                          <JBWDateLabel inputDate={claimData.coverageStartDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {claimData.expirationDate && (
                          <JBWDateLabel inputDate={claimData.expirationDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Existing Claims */}
          {invoice?.invoiceClaims && invoice.invoiceClaims.length > 0 && (
            <Paper elevation={3} sx={{ p: 1 }}>
              <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, fontWeight: 'bold' }}>
                Existing Claims
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Existing Claims">
                  <TableHead>
                    <TableRow>
                      <TableCell>Jbw Claim Id</TableCell>
                      <TableCell>Agreement Id</TableCell>
                      <TableCell>Claim Status</TableCell>
                      <TableCell>Claim Link</TableCell>
                      <TableCell>Claim Amount</TableCell>
                      <TableCell>Paid Amount</TableCell>
                      <TableCell>Claim Filed Date</TableCell>
                      <TableCell>Claim Status Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.invoiceClaims.map((row, index) => (
                      <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>{row.jbwClaimId}</TableCell>
                        <TableCell>{row.jbwAgreementId}</TableCell>
                        <TableCell>{row.claimStatus}</TableCell>
                        <TableCell>{row.claimLink}</TableCell>
                        <TableCell>{row.claimAmount}</TableCell>
                        <TableCell>{row.paidAmount}</TableCell>
                        <TableCell>
                          {row.claimFiledDate && (
                            <JBWDateLabel inputDate={row.claimFiledDate} dateFormat="MM/DD/YYYY" />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.claimStatusDate && (
                            <JBWDateLabel inputDate={row.claimStatusDate} dateFormat="MM/DD/YYYY" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleDuplicateClaim}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DuplicateClaimDialog
